import { useSearchParams } from 'react-router';
import { type VehicleCycledEnergyRecord } from '@/features/BatteryMonitoring/services/battery-monitoring-api/types/battery-monitoring-api';

export type FilterKey =
  | 'country'
  | 'customer'
  | 'chassisFilter'
  | 'stateOfHealthIndex'
  | 'cycledEnergyPerKilometerIndex'
  | 'battery'
  | 'configuration'
  | 'vehicleType'
  | 'propulsion'
  | 'chassisNumber'
  | 'sortColumn'
  | 'sortDir'
  | 'rowsPerPage'
  | 'page'
  | 'showAlerts';

export type UpdateQueryParameterValues = (parameters: { filterKeys: Partial<Record<FilterKey, string[]>> }) => void;

interface UseUpdateQueryParameterValuesProperties {
  readonly vehicles?: VehicleCycledEnergyRecord[];
}

/**
 * Build a quick lookup table of customer -> country.
 * Used to drop customers when their country is excluded.
 */
const buildCustomerCountryMap = (vehicles: VehicleCycledEnergyRecord[]): Record<string, string> => {
  const customerCountry: Record<string, string> = {};

  for (const vehicle of vehicles) {
    customerCountry[vehicle.customer] = vehicle.customerCountry;
  }

  return customerCountry;
};

const getNextCustomers = ({
  previous,
  filterKeys,
  customerCountry,
}: {
  previous: URLSearchParams;
  filterKeys: Partial<Record<FilterKey, string[]>>;
  customerCountry: Record<string, string>;
}): string[] => {
  const selectedCountries = filterKeys.country ?? previous.getAll('country');
  const selectedCustomers = filterKeys.customer ?? previous.getAll('customer');

  if (selectedCountries.length === 0) {
    return selectedCustomers;
  }

  return selectedCustomers.filter((customer) => {
    const country = customerCountry[customer];
    return !country || selectedCountries.includes(country);
  });
};

const buildValidChassisSet = ({
  vehicles,
  selectedCountries,
  selectedCustomers,
}: {
  vehicles: VehicleCycledEnergyRecord[];
  selectedCountries: string[];
  selectedCustomers: string[];
}): Set<string> => {
  const hasCountryFilter = selectedCountries.length > 0;
  const hasCustomerFilter = selectedCustomers.length > 0;
  const validChassis = new Set<string>();

  for (const vehicle of vehicles) {
    const matchesCountry = !hasCountryFilter || selectedCountries.includes(vehicle.customerCountry);
    const matchesCustomer = !hasCustomerFilter || selectedCustomers.includes(vehicle.customer);

    if (matchesCountry && matchesCustomer) {
      validChassis.add(vehicle.chassisNumber);
    }
  }

  return validChassis;
};

/**
 * Determines whether an existing query parameter should be ignored
 * when rebuilding the new URLSearchParams object.
 */
const shouldSkipExistingValue = ({
  oldKey,
  oldValue,
  filterKeys,
  customerCountry,
  validChassis,
}: {
  oldKey: string;
  oldValue: string;
  filterKeys: Partial<Record<FilterKey, string[]>>;
  customerCountry: Record<string, string>;
  validChassis?: Set<string>;
}): boolean => {
  // Drop customers that belong to countries no longer selected
  if (filterKeys.country && oldKey === 'customer') {
    const country = customerCountry[oldValue];
    if (country && !filterKeys.country.includes(country)) {
      return true;
    }
  }

  if (validChassis && oldKey === 'chassisFilter' && !validChassis.has(oldValue)) {
    return true;
  }

  // If this filter is being updated, skip its previous values
  return oldKey in filterKeys;
};

/**
 * Applies the new filter values (removing old ones first).
 */
const applyFilterKeys = (parameters: URLSearchParams, filterKeys: Partial<Record<FilterKey, string[]>>): void => {
  for (const [key, values] of Object.entries(filterKeys) as [filterKey: FilterKey, values: string[]][]) {
    parameters.delete(key);
    for (const value of values) {
      parameters.append(key, value);
    }
  }
};

/**
 * Any time one of these filter keys changes,
 * pagination should be reset (remove `page` query parameter entirely).
 */
const resetPageFilterKeys = new Set<FilterKey>([
  'country',
  'customer',
  'chassisFilter',
  'stateOfHealthIndex',
  'cycledEnergyPerKilometerIndex',
  'rowsPerPage',
  'battery',
  'configuration',
  'vehicleType',
  'propulsion',
  'showAlerts',
]);

const useUpdateQueryParameterValues = ({
  vehicles,
}: UseUpdateQueryParameterValuesProperties): UpdateQueryParameterValues => {
  const [, setSearchParameters] = useSearchParams();

  const updateQueryParameterValues: UpdateQueryParameterValues = ({ filterKeys }) => {
    // Nothing to update if vehicles aren't loaded or no filter keys passed
    if (!vehicles || Object.keys(filterKeys).length === 0) {
      return;
    }

    // Determine whether this update requires clearing the current page
    const shouldResetPage = Object.keys(filterKeys).some((key) => resetPageFilterKeys.has(key as FilterKey));

    setSearchParameters((previous) => {
      const oldParameters = new URLSearchParams(previous);
      const newParameters = new URLSearchParams();

      const customerCountry = buildCustomerCountryMap(vehicles);
      const validChassis =
        filterKeys.country || filterKeys.customer
          ? buildValidChassisSet({
              vehicles,
              selectedCountries: filterKeys.country ?? oldParameters.getAll('country'),
              selectedCustomers: getNextCustomers({
                previous: oldParameters,
                filterKeys,
                customerCountry,
              }),
            })
          : undefined;

      // Rebuild parameters while skipping those that should be replaced or removed
      for (const [oldKey, oldValue] of oldParameters.entries()) {
        // Always remove page if major filters changed
        if (shouldResetPage && oldKey === 'page') {
          continue;
        }

        if (
          shouldSkipExistingValue({
            oldKey,
            oldValue,
            filterKeys,
            customerCountry,
            validChassis,
          })
        ) {
          continue;
        }

        newParameters.append(oldKey, oldValue);
      }

      // Prevent page from being applied in the same update
      const filterKeysToApply: Partial<Record<FilterKey, string[]>> = { ...filterKeys };
      if (shouldResetPage) {
        delete filterKeysToApply.page;
      }

      // Apply new filter values
      applyFilterKeys(newParameters, filterKeysToApply);

      // Always remove 'code' and 'state' parameters from Cognito when updating filters
      newParameters.delete('code');
      newParameters.delete('state');

      return newParameters;
    });
  };

  return updateQueryParameterValues;
};

export default useUpdateQueryParameterValues;
